// src/llmConnections/components/LlmConnectionCard.jsx
//
// One registered LLM connection. Same card shape as
// connectors/components/ConnectionCard.jsx, plus a level chip (org/account/
// project/personal) since that's the one thing LLM connections have that
// the other families don't — everything else (status, last-tested,
// disabled Deactivate) follows the same pattern for the same reason
// (no backend support for either).

import PropTypes from "prop-types";
import { RefreshCw, Power } from "lucide-react";
import { StatusBadge, Button } from "../../shared/ui";
import { getLlmProvider } from "../constants";

const LEVEL_STYLE = {
  org: { label: "Organization", className: "bg-purple-100 text-purple-700" },
  account: { label: "Account", className: "bg-blue-100 text-blue-700" },
  project: { label: "Project", className: "bg-teal-100 text-teal-700" },
  personal: { label: "Personal", className: "bg-gray-100 text-gray-600" },
};

function formatTimestamp(iso) {
  if (!iso) return "Never tested";
  const d = new Date(iso);
  return `Tested ${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
}

export default function LlmConnectionCard({ connection, onTest, testing }) {
  const provider = getLlmProvider(connection.provider);
  const level = LEVEL_STYLE[connection.level] || LEVEL_STYLE.personal;

  return (
    <div className="bg-white border border-line rounded-xl p-4 flex flex-col gap-3 shadow-[var(--jade-sh-1)]">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-ink truncate">{connection.name}</p>
          <p className="text-xs text-muted mt-0.5">{provider?.label ?? connection.provider}</p>
        </div>
        <StatusBadge map="connection" status={testing ? "testing" : connection.status} size="sm" />
      </div>

      <div className="flex items-center gap-1.5 flex-wrap">
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${level.className}`}>
          {level.label}
        </span>
        {connection.purpose && (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-gray-100 text-gray-600">
            {connection.purpose}
          </span>
        )}
      </div>

      {connection.status === "failed" && connection.lastError && (
        <p className="text-xs text-status-critical bg-red-50 border border-red-100 rounded-lg px-2.5 py-1.5">
          {connection.lastError}
        </p>
      )}

      <div className="flex items-center justify-between gap-2 pt-1 border-t border-line">
        <span className="text-[11px] text-muted">{formatTimestamp(connection.lastTestedAt)}</span>
        <div className="flex items-center gap-1.5">
          <Button variant="secondary" size="sm" onClick={() => onTest(connection)} disabled={testing} loading={testing}>
            <RefreshCw size={13} /> Test
          </Button>
          <Button variant="ghost" size="sm" disabled title="Not available yet">
            <Power size={13} /> Deactivate
          </Button>
        </div>
      </div>
    </div>
  );
}

LlmConnectionCard.propTypes = {
  connection: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
    provider: PropTypes.string.isRequired,
    purpose: PropTypes.string,
    level: PropTypes.string,
    status: PropTypes.string,
    lastTestedAt: PropTypes.string,
    lastError: PropTypes.string,
  }).isRequired,
  onTest: PropTypes.func.isRequired,
  testing: PropTypes.bool,
};
